import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import KineticTitle from "@/components/KineticTitle";
import HeroCanvas from "@/components/HeroCanvas";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import MethodSection from "@/components/MethodSection";
import CTASection from "@/components/CTASection";
import ArticleRow from "@/components/ArticleRow";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/95d397dc-b9ff-404d-b038-03b8ada7fd28/images/a05ea9d325fbbe8811a04985e788531ae0cf43cd588757ab8a18b16fa22c3a91.jpeg";

export default function Home() {
  useSEO("home");
  const { tr } = useLang();
  const h = tr("home");
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setLatest((r.data || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 60, damping: 14 });
  const sry = useSpring(ry, { stiffness: 60, damping: 14 });

  const onVisualMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 5);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 5);
  };
  const onVisualLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <>
      {/* HERO — Network to Clarity */}
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy noise">
        <div className="pointer-events-none absolute -left-40 top-1/3 h-[560px] w-[560px] rounded-full bg-gold/10 blur-[140px]" />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-20 pt-28 lg:grid-cols-12 lg:px-10 lg:pt-32">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-10 bg-gold/70" />
              {h.eyebrow}
            </motion.p>

            <KineticTitle
              key={h.eyebrow}
              lines={h.titleLines || []}
              accentIndex={2}
              delay={0.25}
              className="font-serif text-4xl leading-[1.08] tracking-tight text-ivory sm:text-5xl lg:text-[4.1rem]"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-ink-light/70 sm:text-lg"
              data-testid="hero-sub"
            >
              {h.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-bright"
              >
                {h.ctaPrimary}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/insights"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-gold/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-gold-bright transition-colors hover:bg-gold/10"
              >
                {h.ctaSecondary}
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-6" style={{ perspective: 1200 }}>
            <motion.div style={{ rotateX: srx, rotateY: sry }} onMouseMove={onVisualMove} onMouseLeave={onVisualLeave} className="relative" data-testid="hero-visual">
              <div className="frame-clipped relative aspect-[4/3] overflow-hidden border border-gold/25 lg:aspect-[5/4]">
                <motion.img
                  src={HERO_IMG}
                  alt="Network to Clarity — rotte commerciali che convergono in un punto di decisione"
                  style={{ y: imgY }}
                  className="h-full w-full scale-110 object-cover opacity-80"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  data-testid="hero-image"
                />
                <HeroCanvas className="absolute inset-0 h-full w-full mix-blend-screen" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/25" />
              </div>
              <div className="absolute -left-3 -top-3 h-9 w-9 border-l-2 border-t-2 border-gold" />
              <div className="absolute -bottom-3 -right-3 h-9 w-9 border-b-2 border-r-2 border-gold" />
              <div className="absolute bottom-5 left-5 border border-gold/20 bg-navy/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-bright/90 backdrop-blur">
                Network to Clarity
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-ivory/40"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{h.scrollHint}</span>
          <span className="h-8 w-px bg-gradient-to-b from-gold/70 to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      {/* 3 PILLARS — light editorial */}
      <section data-testid="pillars-section" className="bg-ivory py-20 text-ink lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">
                <span className="h-px w-10 bg-gold" />
                {h.pillarsEyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl tracking-tight sm:text-4xl lg:text-5xl">
                {h.pillarsTitle?.[0]} <em className="text-[#8a7448]">{h.pillarsTitle?.[1]}</em>
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-6 md:grid-cols-3 lg:gap-8" data-testid="pillars-grid">
            {(h.pillars || []).map((p, i) => (
              <Reveal key={p.label} delay={i * 0.12} className="h-full">
                <div className="group h-full border border-ink/10 bg-ivory-light p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_18px_50px_-20px_rgba(7,21,34,0.35)] lg:p-10">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm tracking-[0.2em] text-gold">{p.num}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted/60">{p.label}</span>
                  </div>
                  <h3 className="mt-10 font-serif text-2xl text-ink lg:text-3xl">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink-muted lg:text-base">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW — navy editorial split */}
      <section data-testid="about-preview" className="relative overflow-hidden bg-navy py-20 noise lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-10 bg-gold/70" /> {h.aboutEyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                {h.aboutTitle?.[0]}
                <br />
                <em className="text-gold-bright">{h.aboutTitle?.[1]}</em>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                data-testid="about-preview-link"
                className="group mt-10 inline-flex items-center gap-3 border border-gold/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-gold-bright transition-colors hover:bg-gold/10"
              >
                {h.aboutLink}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <p className="text-lg leading-relaxed text-ivory/75 lg:text-xl">{h.aboutText}</p>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="about-preview-list">
              {(h.aboutList || []).map((item, i) => (
                <Reveal key={item} delay={0.2 + i * 0.08}>
                  <div className="flex items-center gap-4 border-t border-gold/15 py-4">
                    <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                    <span className="font-mono text-xs uppercase tracking-[0.15em] text-ivory/70">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MethodSection />

      {/* KNOWLEDGE PREVIEW */}
      <section data-testid="knowledge-preview" className="bg-navy-light py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal>
                <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                  <span className="h-px w-10 bg-gold/70" /> {h.knowledgeEyebrow}
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif text-3xl tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                  {h.knowledgeTitle?.[0]} <em className="text-gold-bright">{h.knowledgeTitle?.[1]}</em>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link
                to="/knowledge-center"
                data-testid="knowledge-preview-link"
                className="group inline-flex items-center gap-3 border border-gold/40 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold-bright transition-colors hover:bg-gold/10"
              >
                {h.knowledgeLink}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          {latest.length === 0 ? (
            <p className="border-t border-gold/15 py-10 text-sm text-ivory/50">{tr("common").noArticles}</p>
          ) : (
            <div data-testid="knowledge-preview-rows">
              {latest.map((a, i) => (
                <Reveal key={a.id || a.slug} delay={i * 0.08}>
                  <ArticleRow article={a} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
